import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';
import { Box, Button, Container, Typography } from '@material-ui/core';
import { APP_PATH_EDITOR_JOB } from '../../config';
import usePageTitle from '../../hooks/usePageTitle';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8)
  },
  heroSection: {
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(6),
    textAlign: 'left'
  },
  heroTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(3),
    color: theme.palette.text.primary
  },
  heroSubtitle: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.2rem'
    }
  },
  heroDescription: {
    fontSize: '1.125rem',
    marginBottom: theme.spacing(4),
    color: theme.palette.text.primary
  },
  section: {
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(6)
  },
  sectionTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(4),
    textAlign: 'left',
    color: theme.palette.text.primary
  },
  featureTitle: {
    marginTop: theme.spacing(3),
    fontSize: '1.25rem',
    color: theme.palette.text.primary
  },
  featureDescription: {
    color: theme.palette.text.primary,
    fontSize: '1rem',
    lineHeight: 1.7
  },
  benefitsList: {
    listStyle: 'none',
    padding: 0
  },
  benefitItem: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    paddingLeft: theme.spacing(2)
  },
  ctaSection: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
    textAlign: 'center',
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(2),
    marginTop: theme.spacing(6)
  },
  ctaTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    fontSize: '2rem',
    color: theme.palette.text.primary
  },
  ctaDescription: {
    fontSize: '1.125rem',
    marginBottom: theme.spacing(4),
    color: theme.palette.text.primary
  },
  ctaButton: {
    fontSize: '1.25rem',
    padding: theme.spacing(2, 6),
    textTransform: 'none'
  }
}));

const Startups = () => {
  const classes = useStyles();
  usePageTitle('Remote Startups & Companies');

  return (
    <div className={classes.root}>
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box className={classes.heroSection}>
          <Typography variant="h2" className={classes.heroTitle}>
            <span role="img" aria-label="seedling">🌱</span> Empower Your Startup With the Best Global Talent
          </Typography>
          <Typography variant="h4" className={classes.heroSubtitle}>
            Hire remotely. Scale faster. Build smarter.
          </Typography>
          <Typography className={classes.heroDescription}>
            Growing a startup is a race against time and the right talent can
            make all the difference. At Remote Job, we help agile teams of all
            sizes find world-class developers, designers, operators, and product
            builders from anywhere in the world.
          </Typography>
          <Typography className={classes.heroDescription}>
            Whether you're hiring your first engineer, expanding a lean global
            team, or bringing in specialists to accelerate growth, our platform
            connects you with remote professionals who thrive in fast-paced
            environments.
          </Typography>
        </Box>

        {/* Features Section */}
        <Box className={classes.section}>
          <Typography variant="h2" className={classes.sectionTitle}>
            <span role="img" aria-label="rocket">🚀</span> Why Startups Choose Remote Job
          </Typography>
          <Box>
            <Typography
              variant="h6"
              className={classes.featureTitle}
              gutterBottom>
              <span role="img" aria-label="globe">🌍</span> Global Access to Exceptional Talent
            </Typography>
            <Typography className={classes.featureDescription} paragraph>
              Your ideal candidate may be across the world. We help you reach
              them instantly.
            </Typography>

            <Typography
              variant="h6"
              className={classes.featureTitle}
              gutterBottom>
              <span role="img" aria-label="lightning">⚡</span> Built for Startups — Powerful Enough for Enterprises
            </Typography>
            <Typography className={classes.featureDescription} paragraph>
              We understand the urgency, flexibility, and ownership mindset
              startups need while providing the reliability and reach trusted by
              larger companies.
            </Typography>

            <Typography
              variant="h6"
              className={classes.featureTitle}
              gutterBottom>
              <span role="img" aria-label="target">🎯</span> High-Quality Matches, Not Noise
            </Typography>
            <Typography className={classes.featureDescription} paragraph>
              Our platform attracts experienced professionals who value impact,
              speed, and autonomy the exact qualities that fuel startup success.
            </Typography>

            <Typography
              variant="h6"
              className={classes.featureTitle}
              gutterBottom>
              <span role="img" aria-label="briefcase">💼</span> Easy Posting, Fast Results
            </Typography>
            <Typography className={classes.featureDescription} paragraph>
              Create your job post in minutes and start receiving strong
              applicants almost immediately.
            </Typography>

            <Typography
              variant="h6"
              className={classes.featureTitle}
              gutterBottom>
              <span role="img" aria-label="money">💰</span> Budget-Friendly Hiring
            </Typography>
            <Typography className={classes.featureDescription} paragraph>
              Scale your team without draining your runway. Remote talent gives
              you flexibility, savings, and access to world-class expertise.
            </Typography>
          </Box>
        </Box>

        {/* Benefits Section */}
        <Box className={classes.section}>
          <Typography variant="h2" className={classes.sectionTitle}>
            <span role="img" aria-label="lightbulb">💡</span> What Your Startup Can Achieve Here
          </Typography>
          <ul className={classes.benefitsList}>
            <li className={classes.benefitItem}>
              ✓ Build distributed teams across time zones
            </li>
            <li className={classes.benefitItem}>
              ✓ Hire senior-level expertise at startup-friendly costs
            </li>
            <li className={classes.benefitItem}>
              ✓ Attract talent that loves ownership and fast execution
            </li>
            <li className={classes.benefitItem}>
              ✓ Expand beyond local constraints and reach global skill sets
            </li>
            <li className={classes.benefitItem}>
              ✓ Create a strong, diverse, remote-first culture
            </li>
          </ul>
          <Box mt={4}>
            <Typography
              variant="body1"
              style={{ fontSize: '1.125rem', color: '#666' }}>
              No matter your stage pre-seed to Series C and beyond, Remote Job
              connects you with the people who can help bring your vision to
              life.
            </Typography>
          </Box>
        </Box>

        {/* CTA Section */}
        <Box className={classes.ctaSection}>
          <Typography variant="h3" className={classes.ctaTitle}>
            Ready to Hire?
          </Typography>
          <Typography className={classes.ctaDescription}>
            Get your job in front of top remote candidates today.
          </Typography>
          <RouterLink
            to={APP_PATH_EDITOR_JOB}
            style={{ textDecoration: 'none' }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              className={classes.ctaButton}>
              Post Your Job <span role="img" aria-label="sparkles">✨</span>
            </Button>
          </RouterLink>
        </Box>
      </Container>
    </div>
  );
};

export default Startups;
